import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { MdAddCircleOutline, MdDocumentScanner, MdOutlineAssignmentTurnedIn, MdSearch } from "react-icons/md";

import Button from "../../../components/Button";
import CustomDataTable from "../../../components/CustomDataTable";
import LoadingSpinner from "../../../components/LoadingSpinner";
import { USER_TYPE } from "../../../constants/common";
import quizServices from "../../../services/quizServices";
import { getLanguage } from "../../../utils/getLanguage";
import { useNavigateWithLang } from "../../../utils/navigation";

const TeacherQuizzesAndHomeworks = () => {
  const { t } = useTranslation();
  const lang = getLanguage();
  const navigate = useNavigateWithLang();

  const [loading, setLoading] = useState(false);
  const [assessments, setAssessments] = useState([]); // State for assessments
  const [searchText, setSearchText] = useState(""); // State for search text
  const [filteredAssessments, setFilteredAssessments] = useState([]); // Filtered assessments for search

  const getAssessments = async () => {
    setLoading(true);
    try {
      // Replace the real API call with the dummy data for testing
      const response = await quizServices.getQuizzes();
      setAssessments(response.data);
      setFilteredAssessments(response.data); // Initially setting filtered assessments to all data
    } catch (error) {
      console.error("Error fetching assessments:", error);
    } finally {
      setLoading(false);
    }
  };
  const normalizeText = (text) => {
    return text.trim().replace(/\s+/g, " ").toLowerCase();
  };

  const handleSearch = (event) => {
    const rawInput = event.target.value;
    setSearchText(rawInput);

    const normalizedQuery = normalizeText(rawInput);

    const filtered = assessments.filter((assessment) =>
      normalizeText(assessment.name).includes(normalizedQuery)
    );

    setFilteredAssessments(filtered);
  };

  useEffect(() => {
    getAssessments();
  }, [lang]);

  if (loading) {
    return <LoadingSpinner />;
  }

  const columns = [
    {
      name: t("assessment_name"),
      field: "name", // Field for the assessment name
      sortable: true,
      body: (rowData) => (
        <Button
          labelStyle="block w-52 overflow-hidden truncate whitespace-nowrap text-start"
          onClick={() => navigate(`/${USER_TYPE.TEACHER.ROLE}/quizzes-and-homeworks/${rowData?.id}`)}
          variant="link"
          color="blue"
          label={rowData?.name}
          tooltip={rowData?.name}
        />
      ),
    },
    {
      name: t("course_name"),
      field: "course.course", // Field for course name
      sortable: true,
      body: (rowData) => <Button variant="link" color="blue" label={rowData?.course?.course} />,
    },
    {
      name: t("type"),
      field: "type", // Field for type (homework or quiz)
      sortable: true,
      body: (rowData) => <span>{rowData?.type == 1 ? t("quiz") : t("homework")}</span>,
    },
    {
      name: t("status"),
      field: "status", // Field for status (draft or published)
      sortable: true,
      body: (rowData) => (
        <span className={rowData?.status == 1 ? "text-gray-500" : "text-green-500"}>
          {rowData?.status == 1 ? t("draft") : t("published")}
        </span>
      ),
    },
    {
      name: "",
      body: (rowData) => (
        <Button
          color="coral"
          iconLeft={<MdOutlineAssignmentTurnedIn size={20} />}
          label={t("assign_to_classroom")}
          // onClick={() => handleAssignToClassroom(rowData)}
        />
      ),
    },
    {
      name: "",
      body: (rowData) => <Button variant="text" color="white" icon={<MdDocumentScanner size={20} />} />,
    },
  ];

  return (
    <>
      <div className="col-span-12 rounded-xl bg-white shadow-md dark:bg-dark-surface">
        <CustomDataTable
          loading={loading}
          columns={columns}
          data={filteredAssessments} // Using filtered assessments data
          withPagination
          withSort
          search={
            <div className="relative w-1/3">
              <MdSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-xl text-mutedText dark:text-dark-mutedText rtl:left-auto rtl:right-3" />
              <input
                type="text"
                placeholder={t("search")}
                value={searchText}
                onChange={handleSearch}
                className="w-full rounded-md border bg-white py-3 pl-10 pr-3 text-sm placeholder:text-mutedText focus:border-blue-500 focus:ring focus:ring-blue-500 focus:ring-opacity-10 dark:border-dark-background dark:bg-dark-background dark:text-white rtl:pl-3 rtl:pr-10"
              />
            </div>
          }
          actions={
            <Button
              onClick={() => navigate(`/${USER_TYPE.TEACHER.ROLE}/quizzes-and-homeworks/create`)}
              iconLeft={<MdAddCircleOutline size={22} />}
              label={t("create_new_assessment")}
            />
          }
        />
      </div>
    </>
  );
};

export default TeacherQuizzesAndHomeworks;
